import { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { message } from "antd";
import { Creators as CartCreators } from "../../services/redux/cart/actions";
import { Creators as AuthCreators } from "../../services/redux/auth/actions";
import { fetchUsers as fetchChesApi } from "../../services/api/user";
import { checkAuth } from "../../util/helpers/requireAuthentication";
import { applyPendingOrderingFor, shownCheDisagrees, setOrderingForOutcome,
  DESK_CHECKOUT_NOT_FOUND, DESK_CHECKOUT_FAILED,
  getOrderingForOutcome, getRequestedOrderingFor, subscribeOrderingForOutcome,
  loadReapplyState, saveReapplyState, reapplyDecision, reapplyChe } from "../../util/helpers/deskOrderingFor";
import { isInRouteDeskFrame } from "../../util/helpers/deskPanelLook";
import { getDeskAccountStatus, subscribeDeskAccountStatus, mayApplyOrderingFor } from "../../util/helpers/deskLockdown";

// At most this many profile re-reads per page load when the shown CHE
// disagrees with the cart's CHE (see shownCheDisagrees).
const MAX_PROFILE_REREADS = 3;

// Desk build only. Renders nothing; applies the ?ordering_for=<odoo id>
// stashed at app start (see util/helpers/deskOrderingFor.js) once an
// employee is signed in.
//
// It waits for isFetching to be false, i.e. the page's own profile request
// has answered. That answer also carries the server's ordering-for CHE; one
// landing after our change would show the old CHE, so a disagreement
// between the cart's CHE and the shown CHE triggers a fresh profile read.
const DeskOrderingFor = ({ user, isFetching, cartOrderingFor, shownOrderingFor, posting, setOrderingFor, getUserDetails }) => {
  const rereads = useRef(0);
  // How applying Route Desk's CHE last ended, and a tick to look again
  // after a back-off (choosing the CHE again, below).
  const [outcome, setOutcome] = useState(getOrderingForOutcome());
  const [reapplyTick, setReapplyTick] = useState(0);
  useEffect(() => {
    setOutcome(getOrderingForOutcome());
    return subscribeOrderingForOutcome(setOutcome);
  }, []);
  // DeskAccountGuard's verdict: inside the panel the CHE is applied only once
  // the signed-in account is confirmed (or Route Desk said manual).
  const [accountStatus, setAccountStatus] = useState(getDeskAccountStatus());
  useEffect(() => {
    setAccountStatus(getDeskAccountStatus());
    return subscribeDeskAccountStatus(setAccountStatus);
  }, []);

  useEffect(() => {
    if (isFetching) return;
    if (!mayApplyOrderingFor(isInRouteDeskFrame(), accountStatus)) return;
    applyPendingOrderingFor({
      user,
      isAuthenticated: checkAuth(),
      fetchChes: fetchChesApi,
      setOrderingFor,
    }).then((outcome) => {
      if (outcome !== "none") console.log("desk ordering_for:", outcome);
      if (outcome !== "none" && outcome !== "waiting") setOrderingForOutcome(outcome);
      // Same words as the line the cart shows instead of checkout.
      if (outcome === "no-match") message.warning(DESK_CHECKOUT_NOT_FOUND, 8);
      if (outcome === "failed") message.warning(DESK_CHECKOUT_FAILED, 8);
    });
  }, [user, isFetching, setOrderingFor, accountStatus]);

  // The server clears "ordering for" after a paid order or invoice: choose
  // the CHE Route Desk last asked for again, with the same setOrderingFor
  // call as on opening (the cart's own CHE row, or the same exact search).
  // While that is pending the outcome is "reselecting", so the cart and the
  // credit pages say "Waiting for the CHE from Route Desk…"; once the tries
  // are used up it goes back to "set" and their "Could not select the CHE"
  // line shows. Rules and limits: reapplyDecision in
  // util/helpers/deskOrderingFor.js.
  const reapplyInFlight = useRef(false);
  const reapplyState = useRef(undefined);
  useEffect(() => {
    if (!isInRouteDeskFrame()) return undefined;
    // sessionStorage can be blocked: then this simply does nothing.
    const safely = (fn, fallback) => { try { return fn(); } catch (e) { return fallback; } };
    if (reapplyState.current === undefined) reapplyState.current = safely(() => loadReapplyState(), null);
    const requestedId = safely(() => getRequestedOrderingFor(), null);
    const decision = reapplyDecision({
      inFrame: true,
      mayApply: mayApplyOrderingFor(true, accountStatus),
      signedIn: !!user && checkAuth(),
      isFetching,
      posting: posting || reapplyInFlight.current,
      requestedId,
      outcome,
      shownCheId: shownOrderingFor?.odoo_id,
      state: reapplyState.current,
      now: Date.now(),
    });
    if (decision.state && JSON.stringify(decision.state) !== JSON.stringify(reapplyState.current)) {
      reapplyState.current = decision.state;
      safely(() => saveReapplyState(decision.state));
    }
    const setOutcomeSafely = (value) => safely(() => setOrderingForOutcome(value));
    // Done (the server shows a CHE again) or given up: back to the plain rules.
    if (outcome === "reselecting" && (decision.gaveUp || shownOrderingFor?.odoo_id) && !reapplyInFlight.current) {
      setOutcomeSafely("set");
    }
    if (decision.action === "wait") {
      if (outcome === "set") setOutcomeSafely("reselecting");
      const timer = setTimeout(() => setReapplyTick((t) => t + 1), decision.waitMs);
      return () => clearTimeout(timer);
    }
    if (decision.action !== "reapply") return undefined;
    console.log("desk ordering_for: choosing the CHE again");
    if (outcome !== "reselecting") setOutcomeSafely("reselecting");
    reapplyInFlight.current = true;
    reapplyChe({ requestedId, cartChe: cartOrderingFor, user, fetchChes: fetchChesApi, setOrderingFor })
      .then((result) => {
        // A lookup that failed counts as a used try; the back-off retries.
        if (result !== "set") console.log("desk ordering_for: choosing again:", result);
      })
      .finally(() => {
        reapplyInFlight.current = false;
        setReapplyTick((t) => t + 1);
      });
    return undefined;
  }, [user, isFetching, posting, outcome, shownOrderingFor, cartOrderingFor, accountStatus, setOrderingFor, reapplyTick]);

  useEffect(() => {
    if (isFetching || !user?.id || rereads.current >= MAX_PROFILE_REREADS) return;
    if (shownCheDisagrees(cartOrderingFor, shownOrderingFor)) {
      rereads.current += 1;
      getUserDetails(user.id);
    }
  }, [cartOrderingFor, shownOrderingFor, isFetching, user, getUserDetails]);

  return null;
};

const mapStateToProps = (state) => ({
  user: state?.auth?.user,
  isFetching: state?.auth?.isFetching,
  cartOrderingFor: state?.cart?.ordering_for,
  shownOrderingFor: state?.auth?.ordering_for_data,
  posting: state?.cart?.posting_ordering_for,
});

const mapDispatchToProps = (dispatch) => ({
  setOrderingFor: (employee, che) => dispatch(CartCreators.setOrderingFor(employee, che)),
  getUserDetails: (id) => dispatch(AuthCreators.getUserDetails(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DeskOrderingFor);
